import {
  ApplicationConfig,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
  provideAppInitializer,
  inject,
  isDevMode,
} from '@angular/core';
import { firstValueFrom } from 'rxjs';
import {
  provideRouter,
  TitleStrategy,
  withComponentInputBinding,
  withEnabledBlockingInitialNavigation,
  withInMemoryScrolling,
} from '@angular/router';
import { TranslocoTitleStrategy } from './core/title/transloco-title-strategy';

import { routes } from './app.routes';
import { provideKsgUi } from '@ksg/ui';

import { provideHttpClient, withFetch, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { authInterceptor } from './core/auth/auth.interceptor';
import { TranslocoHttpLoader } from './transloco-loader';
import { provideTransloco, TranslocoService } from '@jsverse/transloco';
import { environment } from '../environments/environment';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideRouter(
      routes,
      withComponentInputBinding(),
      withInMemoryScrolling({ anchorScrolling: 'enabled', scrollPositionRestoration: 'enabled' }),
      withEnabledBlockingInitialNavigation(),
    ),
    provideHttpClient(withFetch(), withInterceptors([authInterceptor]), withInterceptorsFromDi()),
    ...environment.mockProviders,
    provideZonelessChangeDetection(),
    provideKsgUi(),
    { provide: TitleStrategy, useClass: TranslocoTitleStrategy },
    provideTransloco({
      config: {
        availableLangs: environment.languages,
        defaultLang: environment.defaultLanguage,
        fallbackLang: environment.defaultLanguage,
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
        missingHandler: {
          useFallbackTranslation: true,
        },
      },
      loader: TranslocoHttpLoader,
    }),
    // Preload the active language before the app renders, so signal-based
    // computed() labels/columns that call transloco.translate() synchronously
    // never memoise raw i18n keys (they'd freeze if evaluated pre-load).
    provideAppInitializer(() => {
      const transloco = inject(TranslocoService);
      return firstValueFrom(transloco.load(transloco.getActiveLang()));
    }),
  ],
};
